import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "devextreme-react/button";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { LoadPanel } from "devextreme-react/load-panel";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import { alert } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import axios from "axios";
import FormField from "../../../../dynamicFormBuilder/FormField";
import { DisableEnableControls, loadLookupFromStaticDataSource } from "../../../../common/Utils";
import { getApiErrorMessage, CONCURRENCY_HANDLED } from "../../../../common/concurrency";
import { MESSAGES } from "../../../../common/Messages";
import data from "../../../../assets/apiRoutes.json";
import { TransManagementProfileRoutes } from "../../../../const/constants";
import { EMPTY_GUID } from "../../../../enum/Enum";
import { PrimaryLayout } from "./PrimaryLayout";
import { TransMgmtTabLayout } from "./TransMgmtTabLayout";
import { TransactionsTabLayout } from "./TransactionsTabLayout";
import { ItemsTabLayout } from "./ItemsTabLayout";
import {
    COLOR,
    DEFAULT_TAB,
    FIELD_OPTIONS,
    ITEM_BASIS_QTY,
    PAGE_TITLE,
    STATUS,
    TRANS_BASIS_DATE,
    VISIBLE_TABS,
    YES_NO,
    defaultTabOptionsFor,
} from "./CodeLists";
import { applyColorFill, decorateColorSelect, isColorField } from "./ColorSwatch";
import { deleteTransManagementProfile } from "./deleteProfile";

// Add / Edit for Delphi WMD0081 "WM Transaction Mgmt Profile" (WMTRANSMANAGEMENTDP).
//
// Page skeleton follows the sibling data-profile screens: the dataEntryMainPage wrapper, a Toolbar
// header and a TabPanel with one FormField per tab. The Delphi screen's four tab sheets become three
// — its '&Primary' sheet held three empty group boxes and no bound controls, so it is omitted
// (approved decision 4) and the top-panel fields it sat under become the Primary tab here.
//
// The endpoints opt into PascalCase (DefaultContractResolver on the controller), so the field names
// below are the DTO's property names verbatim.
//
// Rule 16: the ConcurrencyToken read with the record is held in state and posted back unchanged on
// update, then replaced from the save response; the 409 dialog comes free from the AppContainer
// axios interceptor.
const ROUTES = data.TransManagementDp;
const LISTING_PATH = TransManagementProfileRoutes.List;

// The four fields each Use Color flag governs, and what the Delphi change handlers put in them.
// 'N' blanks and zeroes them and disables the block; 'Y' seeds these values and re-enables it
// (edtColorTransactionsChange / edtColorInventotyTabChange). The two sets are deliberately
// asymmetric — 5/1 hours on Transactions, 50/0 quantity on Items — reproduced as read.
const COLOR_BLOCKS = {
    TransTabUseColors: {
        fields: ["TransTabBasisDate", "TransTabPendingLevel", "TransTabPendingColor", "TransTabWarningLevel", "TransTabWarningColor"],
        on: {
            TransTabBasisDate: TRANS_BASIS_DATE.RCV_SHIP_DATE,
            TransTabPendingColor: COLOR.GREEN,
            TransTabWarningColor: COLOR.RED,
            TransTabPendingLevel: 5,
            TransTabWarningLevel: 1,
        },
        off: {
            TransTabBasisDate: "",
            TransTabPendingColor: "",
            TransTabWarningColor: "",
            TransTabPendingLevel: 0,
            TransTabWarningLevel: 0,
        },
    },
    ItemTabUseColors: {
        fields: ["ItemTabBasisQty", "ItemTabPendingLevel", "ItemTabPendingColor", "ItemTabWarningLevel", "ItemTabWarningColor"],
        on: {
            ItemTabBasisQty: ITEM_BASIS_QTY.ON_HAND,
            ItemTabPendingColor: COLOR.GREEN,
            ItemTabWarningColor: COLOR.RED,
            ItemTabPendingLevel: 50,
            ItemTabWarningLevel: 0,
        },
        off: {
            ItemTabBasisQty: "",
            ItemTabPendingColor: "",
            ItemTabWarningColor: "",
            ItemTabPendingLevel: 0,
            ItemTabWarningLevel: 0,
        },
    },
};

const NUMBER_FIELDS = ["TransTabPendingLevel", "TransTabWarningLevel", "ItemTabPendingLevel", "ItemTabWarningLevel"];

/**
 * A new record, with the defaults the Delphi screen stamped onto the dataset before the user ever
 * saw it (qeWMTransactionManagementDPAfterInsert). Seeding them here rather than in the service is
 * the faithful port: the user sees them, and can change them, before the first save.
 *
 * The Delphi handler does not reset TRANSTABWARNINGCOLOR while it does reset the other three colour
 * codes — an oversight that only shows up in a shared dataset buffer carrying the previous record's
 * value. A web form starts from this object, so the asymmetry has nothing to leak and the field is
 * simply blank like its three siblings.
 */
const buildInitialFields = () => ({
    RowId: EMPTY_GUID,
    RecordNumber: "",
    ProfileCode: "",
    Description: "",
    Comment: "",
    Status: STATUS.ACTIVE,

    TabDefaults: VISIBLE_TABS.RECEIPTS_SHIPMENTS_ALL,
    TransTabDefaults: DEFAULT_TAB.OPEN_RECEIPTS,
    EdiOrdersTab: YES_NO.YES,
    WebOrdersTab: YES_NO.YES,
    CustomersTab: YES_NO.YES,
    ItemTab: YES_NO.YES,
    LotTab: YES_NO.YES,
    SubLotTab: YES_NO.YES,
    LocationTab: YES_NO.YES,
    UninvoicedChargesTab: YES_NO.YES,

    TransTabUseColors: YES_NO.NO,
    TransTabBasisDate: "",
    TransTabPendingLevel: 0,
    TransTabPendingColor: "",
    TransTabWarningLevel: 0,
    TransTabWarningColor: "",

    ItemTabUseColors: YES_NO.NO,
    ItemTabBasisQty: "",
    ItemTabPendingLevel: 0,
    ItemTabPendingColor: "",
    ItemTabWarningLevel: 0,
    ItemTabWarningColor: "",

    ConcurrencyToken: null,
});

/**
 * Merges a loaded or saved record over the field shape above so every input stays controlled. A null
 * column becomes "" for the text and coded fields and null for the four numeric levels, which is what
 * the DevExtreme editors expect for "nothing entered".
 *
 * Only the keys the form owns are kept: a save response also carries StatusCode and Message, which
 * would otherwise settle into the form state and be posted back on the next save.
 */
const mergeLoaded = (record) => {
    const merged = buildInitialFields();
    Object.keys(merged).forEach((key) => {
        const value = record ? record[key] : undefined;
        if (value !== null && value !== undefined) {
            merged[key] = value;
            return;
        }
        if (key === "ConcurrencyToken") {
            merged[key] = null;
            return;
        }
        merged[key] = NUMBER_FIELDS.includes(key) ? null : "";
    });
    return merged;
};

const loadPanelPosition = { of: "#accordion" };

function Form(props) {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const history = useHistory();
    const location = useLocation();
    const formRefs = useRef({});

    // The edit route carries the key; its absence means Add.
    const routeRowId = new URLSearchParams(location.search).get("id");

    const [formFields, setformFields] = useState(buildInitialFields());
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);

    // FormField calls onFormLoad from a mount-time effect, so it keeps invoking the callback it was
    // first handed — one whose closure still holds the Add defaults above. The deferred work in
    // loadTabLookups reads the live values through this ref instead.
    const formFieldsRef = useRef(formFields);
    formFieldsRef.current = formFields;

    // Whether the record exists yet is read from the key the form is holding, not from the route:
    // after an insert the page stays put with the new key in state, and the next Save has to be an
    // update. Deciding this from the URL instead would re-post the insert and fail on the duplicate
    // Profile Code check.
    const isSaved = !!formFields.RowId && formFields.RowId !== EMPTY_GUID;

    const loadRecord = useCallback((id) => {
        if (!id || id === EMPTY_GUID) return;

        setLoadPanelVisible(true);
        axios.get(
            `${window._env_.REACT_APP_APIS}/${ROUTES.GetTransManagementDp}?rowId=${id}`,
            { headers: { "Content-Type": "application/json", access_token: accessToken } },
        )
            .then((res) => {
                if (!res.data) {
                    alert(MESSAGES.RECORD_NOT_FOUND, MESSAGES.ERROR);
                    return;
                }
                setformFields(mergeLoaded(res.data));
            })
            .catch((err) => {
                if (err?.[CONCURRENCY_HANDLED]) return;
                alert(getApiErrorMessage(err, MESSAGES.ERROR), MESSAGES.ERROR);
            })
            .finally(() => setLoadPanelVisible(false));
    }, [accessToken]);

    useEffect(() => {
        loadRecord(routeRowId);
    }, [loadRecord, routeRowId]);

    /** Attaches an option list to one of the dynamic form's SelectBoxes. */
    const loadLookupClient = useCallback((name, options) => {
        loadLookupFromStaticDataSource(name, options, null, formRefs, "id", "text");

        // The four colour columns also show the colour itself, not just its name.
        if (isColorField(name)) decorateColorSelect(formRefs.current?.[name]?.instance?.());
    }, []);

    /**
     * uWMD0081.PAS SetDefaultTab, client half: the Default Tab list holds only the tabs the current
     * Visible Tabs selection shows, and the control is disabled outright when that leaves one choice
     * (All Transactions only). The same table is enforced server-side, so this is convenience rather
     * than the only guard.
     */
    const applyDefaultTabRule = useCallback((visibleTabs) => {
        loadLookupClient("TransTabDefaults", defaultTabOptionsFor(visibleTabs));
        DisableEnableControls("TransTabDefaults", visibleTabs === VISIBLE_TABS.ALL_ONLY, formRefs);
    }, [loadLookupClient]);

    /**
     * Enables or disables a Use Color block's dependent controls. Only an explicit 'N' disables it
     * (uWMD0081.PAS:347 tests AsChar = 'N'), so a legacy row with a NULL flag stays editable.
     *
     * A disabled colour dropdown loses its fill through colorSwatch.css, which keys off DevExtreme's
     * dx-state-disabled class, so nothing has to be repainted here.
     */
    const applyColorBlockState = useCallback((flagName, flagValue) => {
        const block = COLOR_BLOCKS[flagName];
        if (!block) return;
        block.fields.forEach((name) => DisableEnableControls(name, flagValue === YES_NO.NO, formRefs));
    }, []);

    // Re-apply both rules whenever the values they depend on change — on load, on reset, and after a
    // save replaces the record.
    useEffect(() => {
        applyDefaultTabRule(formFields.TabDefaults);
    }, [applyDefaultTabRule, formFields.TabDefaults]);

    useEffect(() => {
        applyColorBlockState("TransTabUseColors", formFields.TransTabUseColors);
    }, [applyColorBlockState, formFields.TransTabUseColors]);

    useEffect(() => {
        applyColorBlockState("ItemTabUseColors", formFields.ItemTabUseColors);
    }, [applyColorBlockState, formFields.ItemTabUseColors]);

    // The colour fill follows the value. A pick arrives here through the dynamic form's onValueChanged
    // -> handleFieldChange, and a load, reset or save replaces the same values, so this one effect
    // covers every way a colour can change. A tab that is not mounted has no widget yet; it is filled
    // when it mounts, by loadLookupClient.
    useEffect(() => {
        [
            ["TransTabPendingColor", formFields.TransTabPendingColor],
            ["TransTabWarningColor", formFields.TransTabWarningColor],
            ["ItemTabPendingColor", formFields.ItemTabPendingColor],
            ["ItemTabWarningColor", formFields.ItemTabWarningColor],
        ].forEach(([name, code]) => applyColorFill(formRefs.current?.[name]?.instance?.(), code));
    }, [
        formFields.TransTabPendingColor,
        formFields.TransTabWarningColor,
        formFields.ItemTabPendingColor,
        formFields.ItemTabWarningColor,
    ]);

    const handleFieldChange = useCallback((name, value) => {
        setformFields((prev) => {
            const next = { ...prev, [name]: value };

            // Visible Tabs changed (edtTABDEFAULTSChange): if the default tab it was carrying is no
            // longer one of the visible tabs, fall back the way the Delphi did — to the second entry
            // of the narrowed list, or its only entry when that is all there is.
            if (name === "TabDefaults") {
                const allowed = defaultTabOptionsFor(value).map((o) => o.id);
                if (!allowed.includes(next.TransTabDefaults)) {
                    next.TransTabDefaults = allowed.length > 1 ? allowed[1] : allowed[0];
                }
            }

            // Use Color toggled: seed or clear the block it governs, exactly as the Delphi change
            // handler did. The service repeats the clearing half, so a direct API write cannot leave
            // thresholds behind on a profile that says it uses no colours.
            const block = COLOR_BLOCKS[name];
            if (block && value === YES_NO.YES) Object.assign(next, block.on);
            if (block && value === YES_NO.NO) Object.assign(next, block.off);

            return next;
        });
    }, []);

    // Each tab seeds its own dropdowns as it mounts; the effects above then keep the dependent ones
    // in step. Driven off the layout so a new field needs no change here.
    const loadTabLookups = useCallback((layout) => {
        setTimeout(() => {
            // Read when the timer fires, not when the tab mounted: on Edit the record usually lands
            // in between, and the values captured at mount are the Add defaults (Use Color = N,
            // Visible Tabs = A). Applying those would disable a Use Color = Y profile's thresholds
            // and re-enable a Visible Tabs = D profile's Default Tab, and no later effect would
            // correct it because the record's values do not change again.
            const current = formFieldsRef.current;

            layout.forEach((group) => group.fields.forEach((field) => {
                const options = FIELD_OPTIONS[field.name];
                if (field.type !== "select" || !options) return;

                loadLookupClient(field.name, field.name === "TransTabDefaults"
                    ? defaultTabOptionsFor(current.TabDefaults)
                    : options);
            }));

            // A tab's controls do not exist until that tab has mounted, so the effects that enforce
            // the dependent-control rules on load ran against nothing and silently did nothing. Re-run
            // them here, once the widgets are up: without this, opening a profile that already has Use
            // Color = N shows its Basis Date, Hours and Colour fields fully enabled (uWMD0081.PAS
            // disables all of them). Each call no-ops for the tabs whose controls are not present.
            applyDefaultTabRule(current.TabDefaults);
            applyColorBlockState("TransTabUseColors", current.TransTabUseColors);
            applyColorBlockState("ItemTabUseColors", current.ItemTabUseColors);
        }, 100);
    }, [loadLookupClient, applyDefaultTabRule, applyColorBlockState]);

    const handlePrimaryFormLoad = useCallback(() => loadTabLookups(PrimaryLayout), [loadTabLookups]);
    const handleTransMgmtFormLoad = useCallback(() => loadTabLookups(TransMgmtTabLayout), [loadTabLookups]);
    const handleTransactionsFormLoad = useCallback(() => loadTabLookups(TransactionsTabLayout), [loadTabLookups]);
    const handleItemsFormLoad = useCallback(() => loadTabLookups(ItemsTabLayout), [loadTabLookups]);

    const goBack = useCallback(() => {
        // Returning to the listing restores the search that brought the user here (rule 17).
        history.push(LISTING_PATH);
    }, [history]);

    /**
     * Reset means "undo what I typed", so on an existing record it re-reads the saved row rather
     * than dropping the insert defaults over it — the same behaviour as weekSettings/Form.js. On a
     * new record there is nothing saved, so it goes back to the Delphi AfterInsert defaults.
     */
    const handleReset = useCallback(() => {
        if (isSaved) {
            loadRecord(formFields.RowId);
            return;
        }
        setformFields(buildInitialFields());
    }, [isSaved, loadRecord, formFields.RowId]);

    /**
     * Create from inside the form, same as the listing's Create button: drops the open record and
     * starts a new one from the Delphi AfterInsert defaults. RowId goes back to EMPTY_GUID, so the
     * next Save inserts, and the ConcurrencyToken is cleared.
     *
     * The Create and Edit routes render this same component, so moving to the Create route does not
     * remount it — the state has to be reset here rather than left to a fresh mount.
     */
    const handleCreate = useCallback(() => {
        setformFields(buildInitialFields());
        if (routeRowId) history.push(TransManagementProfileRoutes.Create);
    }, [history, routeRowId]);

    const save = () => {
        // The service enforces the same rule, so a direct API call is refused too.
        if (!formFields.ProfileCode || !formFields.ProfileCode.trim()) {
            alert(MESSAGES.ERROR_FORM_SUBMISSION, MESSAGES.ERROR);
            return;
        }

        const url = isSaved ? ROUTES.UpdateTransManagementDp : ROUTES.InsertTransManagementDp;
        const payload = { ...formFields, RowId: isSaved ? formFields.RowId : EMPTY_GUID };

        setLoadPanelVisible(true);
        axios.post(
            `${window._env_.REACT_APP_APIS}/${url}`,
            payload,
            { headers: { "Content-Type": "application/json", access_token: accessToken } },
        )
            .then((res) => {
                // Stay on the record after saving, as the sibling profile screens do — the user
                // usually carries on editing, and an insert needs its new RowId in hand.
                //
                // The response is the saved row (TransManagementDpResponse derives from the DTO), so
                // adopting it also picks up the refreshed ConcurrencyToken. Without that the next save
                // would echo the token the page was opened with and falsely conflict (rule 16).
                if (res.data) {
                    setformFields(mergeLoaded(res.data));
                }
                notify(res.data?.Message || MESSAGES.UPDATE_SUCCESS, "success", 3000);
            })
            .catch((err) => {
                // A 409 is handled by the shared interceptor, which shows the conflict dialog.
                if (err?.[CONCURRENCY_HANDLED]) return;
                alert(getApiErrorMessage(err, MESSAGES.ERROR), MESSAGES.ERROR);
            })
            .finally(() => setLoadPanelVisible(false));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        save();
    };

    const handleDelete = () => {
        if (!isSaved) return;

        // Rule 7: a successful delete returns to the listing.
        deleteTransManagementProfile(formFields.RowId, accessToken, setLoadPanelVisible).then((deleted) => {
            if (deleted) goBack();
        });
    };

    const title = formFields.ProfileCode
        ? `${PAGE_TITLE} - ${formFields.ProfileCode.trim()}`
        : PAGE_TITLE;

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={loadPanelPosition}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={handleFormSubmit}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>
                                            {title}
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button
                                        text="Save"
                                        useSubmitBehavior={true}
                                        type="default"
                                        className="accordianButton"
                                    />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleCreate} text="New" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button
                                        className="accordianButton"
                                        onClick={handleDelete}
                                        text="Delete"
                                        type="default"
                                        disabled={!isSaved}
                                    />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>

                            {/* deferRendering off so the dependent-dropdown and enable/disable rules can
                                reach every tab's controls, not only the one the user has opened. */}
                            <TabPanel deferRendering={false} showNavButtons={true} height={"auto"}>
                                <TabItem title="Primary">
                                    <FormField
                                        currrentUser={currentUser}
                                        layoutId="0081b1d9-7c25-4a63-9e04-0081ccdd3344"
                                        layoutName="WMTransManagementProfilePrimaryForm"
                                        defaultFormSchema={PrimaryLayout}
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        handleFieldValueChange={handleFieldChange}
                                        DYNAMICFORMSETTING={dynamicFormSetting}
                                        formRefs={formRefs}
                                        onFormLoad={handlePrimaryFormLoad}
                                    />
                                </TabItem>
                                <TabItem title="Trans Mgmt">
                                    <FormField
                                        currrentUser={currentUser}
                                        layoutId="0081c2ea-8d36-4b74-8f15-0081eeff5566"
                                        layoutName="WMTransManagementProfileTransMgmtForm"
                                        defaultFormSchema={TransMgmtTabLayout}
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        handleFieldValueChange={handleFieldChange}
                                        DYNAMICFORMSETTING={dynamicFormSetting}
                                        formRefs={formRefs}
                                        onFormLoad={handleTransMgmtFormLoad}
                                    />
                                </TabItem>
                                <TabItem title="Transactions">
                                    <FormField
                                        currrentUser={currentUser}
                                        layoutId="0081d3fb-9e47-4c85-9026-00811a2b7788"
                                        layoutName="WMTransManagementProfileTransactionsForm"
                                        defaultFormSchema={TransactionsTabLayout}
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        handleFieldValueChange={handleFieldChange}
                                        DYNAMICFORMSETTING={dynamicFormSetting}
                                        formRefs={formRefs}
                                        onFormLoad={handleTransactionsFormLoad}
                                    />
                                </TabItem>
                                <TabItem title="Items">
                                    <FormField
                                        currrentUser={currentUser}
                                        layoutId="0081e40c-af58-4d96-8137-00812b3c99aa"
                                        layoutName="WMTransManagementProfileItemsForm"
                                        defaultFormSchema={ItemsTabLayout}
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        handleFieldValueChange={handleFieldChange}
                                        DYNAMICFORMSETTING={dynamicFormSetting}
                                        formRefs={formRefs}
                                        onFormLoad={handleItemsFormLoad}
                                    />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(Form);
